import { useState, useEffect } from "react";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { toast } from "sonner";
import {
  DollarSign,
  TrendingUp,
  AlertTriangle,
  Package,
  Boxes,
} from "lucide-react";
import { API } from "@/App";

export default function Reports({ user }) {
  const [valuation, setValuation] = useState(null);
  const [lowStock, setLowStock] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const [valRes, lowRes] = await Promise.all([
        fetch(`${API}/reports/inventory-valuation`, { credentials: "include" }),
        fetch(`${API}/reports/low-stock`, { credentials: "include" }),
      ]);

      if (valRes.ok) setValuation(await valRes.json());
      if (lowRes.ok) setLowStock(await lowRes.json());
    } catch (error) {
      toast.error("Failed to fetch reports");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value || 0);
  };

  const chartData = valuation?.by_category?.map((cat) => ({
    name: cat.category || "Uncategorized",
    cost: cat.total_cost_value,
    retail: cat.total_retail_value,
    profit: cat.potential_profit,
  })) || [];

  return (
    <Layout user={user}>
      <div className="space-y-6" data-testid="reports-page">
        {/* Header */}
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">
            Reports
          </h1>
          <p className="text-muted-foreground">
            Inventory analytics and insights
          </p>
        </div>

        <Tabs defaultValue="valuation">
          <TabsList className="rounded-sm">
            <TabsTrigger value="valuation" className="rounded-sm">
              <DollarSign className="h-4 w-4 mr-2" />
              Valuation
            </TabsTrigger>
            <TabsTrigger value="low-stock" className="rounded-sm">
              <AlertTriangle className="h-4 w-4 mr-2" />
              Low Stock
            </TabsTrigger>
          </TabsList>

          <TabsContent value="valuation" className="space-y-6 mt-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="rounded-sm border">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-sm bg-blue-500/10 flex items-center justify-center">
                      <Boxes className="h-5 w-5 text-blue-500" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">
                        Total Units
                      </p>
                      <p className="font-data text-2xl font-semibold">
                        {loading ? "-" : (valuation?.totals?.total_units || 0).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="rounded-sm border">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-sm bg-orange-500/10 flex items-center justify-center">
                      <DollarSign className="h-5 w-5 text-orange-500" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">
                        Cost Value
                      </p>
                      <p className="font-data text-2xl font-semibold">
                        {loading ? "-" : formatCurrency(valuation?.totals?.total_cost_value)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="rounded-sm border">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-sm bg-purple-500/10 flex items-center justify-center">
                      <DollarSign className="h-5 w-5 text-purple-500" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">
                        Retail Value
                      </p>
                      <p className="font-data text-2xl font-semibold">
                        {loading ? "-" : formatCurrency(valuation?.totals?.total_retail_value)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="rounded-sm border">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-sm bg-emerald-500/10 flex items-center justify-center">
                      <TrendingUp className="h-5 w-5 text-emerald-500" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">
                        Potential Profit
                      </p>
                      <p className="font-data text-2xl font-semibold text-emerald-500">
                        {loading ? "-" : formatCurrency(valuation?.totals?.potential_profit)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Chart */}
            {chartData.length > 0 && (
              <Card className="rounded-sm border">
                <CardHeader>
                  <CardTitle className="text-base font-heading">Value by Category</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                        <XAxis dataKey="name" className="text-xs" />
                        <YAxis className="text-xs" tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "hsl(var(--card))",
                            border: "1px solid hsl(var(--border))",
                            borderRadius: "4px",
                          }}
                          formatter={(value) => formatCurrency(value)}
                        />
                        <Bar dataKey="cost" name="Cost Value" fill="#f97316" radius={[2, 2, 0, 0]} />
                        <Bar dataKey="retail" name="Retail Value" fill="#2563eb" radius={[2, 2, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Category Table */}
            <Card className="rounded-sm border">
              <CardHeader>
                <CardTitle className="text-base font-heading">Category Breakdown</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {loading ? (
                  <div className="p-8 space-y-3">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="h-12 skeleton-pulse" />
                    ))}
                  </div>
                ) : valuation?.by_category?.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    No inventory data available
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Category</TableHead>
                        <TableHead className="font-data text-right">Units</TableHead>
                        <TableHead className="font-data text-right">Cost Value</TableHead>
                        <TableHead className="font-data text-right">Retail Value</TableHead>
                        <TableHead className="font-data text-right">Profit</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {valuation?.by_category?.map((cat, index) => (
                        <TableRow key={index} className="data-row">
                          <TableCell>
                            <Badge variant="secondary" className="rounded-sm">
                              {cat.category || "Uncategorized"}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-data text-right">
                            {cat.total_units.toLocaleString()}
                          </TableCell>
                          <TableCell className="font-data text-right">
                            {formatCurrency(cat.total_cost_value)}
                          </TableCell>
                          <TableCell className="font-data text-right">
                            {formatCurrency(cat.total_retail_value)}
                          </TableCell>
                          <TableCell className="font-data text-right text-emerald-500 font-medium">
                            {formatCurrency(cat.potential_profit)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="low-stock" className="space-y-6 mt-6">
            <Card className="rounded-sm border">
              <CardHeader>
                <CardTitle className="text-base font-heading flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-orange-500" />
                  Low Stock Alerts ({lowStock.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {loading ? (
                  <div className="p-8 space-y-3">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="h-12 skeleton-pulse" />
                    ))}
                  </div>
                ) : lowStock.length === 0 ? (
                  <div className="p-8 text-center">
                    <Package className="h-12 w-12 mx-auto mb-4 text-emerald-500" />
                    <p className="text-emerald-500 font-medium">All products well stocked!</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      No items are below their threshold
                    </p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Product</TableHead>
                        <TableHead>Location</TableHead>
                        <TableHead className="font-data text-right">Current</TableHead>
                        <TableHead className="font-data text-right">Threshold</TableHead>
                        <TableHead className="font-data text-right">Shortage</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {lowStock.map((item, index) => (
                        <TableRow key={index} className="data-row">
                          <TableCell>
                            <div>
                              <p className="font-medium">{item.product_name}</p>
                              <p className="text-xs text-muted-foreground font-data">{item.sku}</p>
                            </div>
                          </TableCell>
                          <TableCell>{item.location_name || "Unknown"}</TableCell>
                          <TableCell className="font-data text-right font-semibold text-orange-500">
                            {item.current_stock}
                          </TableCell>
                          <TableCell className="font-data text-right text-muted-foreground">
                            {item.threshold}
                          </TableCell>
                          <TableCell className="font-data text-right">
                            <Badge variant="destructive" className="rounded-sm">
                              -{item.shortage}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
